import { useState, useEffect, useRef, useCallback } from "react";
import { Phone, PhoneOff, Pause, Play, ParkingSquare, ArrowLeftRight, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { callsAPI } from "../../services/api";
import { useCall } from "../layout/ActiveCallOverlay";

const NAVY = "#003366";
const YELLOW = "#FFDA00";
const POLL_MS = 4000;

function fmt(s) {
  const m = Math.floor((s || 0) / 60);
  const sec = (s || 0) % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// A live call taken on the user's DESK PHONE (or another device) mirrored into
// the app so it can be seen and controlled here — the "app + desk phone work as
// one" companion. Hidden whenever the app's own softphone owns the call.
export default function DeskPhoneCallCompanion() {
  const sdk = useCall() || {};
  const appOwnsCall = !!(sdk.activeCall || sdk.incomingCall);

  const [serverCall, setServerCall] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showXfer, setShowXfer] = useState(false);
  const [xferTo, setXferTo] = useState("");
  const [localSecs, setLocalSecs] = useState(0);
  const dismissedSidRef = useRef(null);
  const lastActiveRef = useRef(null);  // tracks the server's current live call

  const poll = useCallback(async () => {
    try {
      const { data } = await callsAPI.getMyActiveCall();
      if (data?.active) {
        lastActiveRef.current = {
          call_sid: data.call_sid, phone: data.caller_number,
          company: data.company_name, seconds: data.seconds,
        };
        setServerCall((prev) => (prev?.call_sid === data.call_sid ? { ...prev, ...data } : data));
        if (typeof data.seconds === "number") setLocalSecs(data.seconds);
      } else {
        // Server says no live call. If we were tracking one, it just ENDED —
        // fire a call-ended event so the app prompts for a quick note.
        const prev = lastActiveRef.current;
        if (prev && (prev.seconds == null || prev.seconds >= 3) && prev.phone) {
          window.dispatchEvent(new CustomEvent("avida:deskcall-ended", { detail: prev }));
        }
        lastActiveRef.current = null;
        setServerCall(null);
      }
    } catch {
      /* transient — keep last known */
    }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  // Local ticking timer between polls.
  useEffect(() => {
    if (!serverCall) return;
    const id = setInterval(() => setLocalSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [serverCall]);

  // Don't show when the app's own softphone has the call, when there's none,
  // or when the user dismissed this specific call.
  const visible = !!serverCall && !appOwnsCall && dismissedSidRef.current !== serverCall.call_sid;
  if (!visible) return null;

  const sid = serverCall.call_sid;
  const onDesk = serverCall.answered_device === "desk";
  const who = serverCall.company_name || serverCall.caller_number || "Active call";

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await poll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that. Try again."); }
    finally { setBusy(false); }
  };

  const doHold = () => act(() => callsAPI.holdCall(sid, !serverCall.on_hold),
    serverCall.on_hold ? "Call resumed" : "Call on hold");
  const doPark = () => act(() => callsAPI.parkCall(sid, null), "Call parked");
  const doHangup = () => act(() => callsAPI.hangupServerCall(sid), "Call ended");
  const doPull = () => act(() => callsAPI.pullCall(sid, "app"), "Pulling call to this device…");
  const doTransfer = async () => {
    const t = xferTo.trim();
    if (!t) return;
    await act(() => callsAPI.transferCall(sid, t), `Transferred to ${t}`);
    setShowXfer(false); setXferTo("");
  };

  return (
    <div
      data-testid="deskphone-call-companion"
      className="fixed left-4 bottom-4 z-[57] w-[300px] rounded-2xl shadow-2xl overflow-hidden"
      style={{ background: NAVY, color: "#fff" }}
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-white/70 truncate">
            {onDesk ? "On your desk phone" : "Active call"}
          </span>
        </div>
        <button data-testid="companion-dismiss" onClick={() => { dismissedSidRef.current = sid; setServerCall(null); }}
          className="text-white/50 hover:text-white" title="Hide">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,218,0,0.15)" }}>
            <Phone className="w-4 h-4" style={{ color: YELLOW }} />
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-black truncate" data-testid="companion-who">{who}</p>
            <p className="text-[11px] text-white/70 tabular-nums">
              {serverCall.on_hold ? "On hold · " : ""}{fmt(localSecs)}
              {serverCall.caller_number && serverCall.company_name ? ` · ${serverCall.caller_number}` : ""}
            </p>
          </div>
        </div>
      </div>

      {showXfer ? (
        <div className="px-4 pb-4 flex items-center gap-2">
          <input
            autoFocus data-testid="companion-xfer-input" value={xferTo}
            onChange={(e) => setXferTo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doTransfer()}
            placeholder="Extension or number"
            className="flex-1 h-9 rounded-lg px-2 text-[13px] text-slate-900 focus:outline-none"
          />
          <button data-testid="companion-xfer-go" disabled={busy || !xferTo.trim()} onClick={doTransfer}
            className="h-9 px-3 rounded-lg text-[12px] font-bold disabled:opacity-50" style={{ background: YELLOW, color: NAVY }}>Go</button>
          <button onClick={() => { setShowXfer(false); setXferTo(""); }} className="h-9 px-2 rounded-lg text-[12px] text-white/70">Cancel</button>
        </div>
      ) : (
        <div className="px-3 pb-3 grid grid-cols-4 gap-1.5">
          <CompanionBtn testid="companion-hold" icon={serverCall.on_hold ? Play : Pause} label={serverCall.on_hold ? "Resume" : "Hold"} onClick={doHold} disabled={busy} />
          <CompanionBtn testid="companion-park" icon={ParkingSquare} label="Park" onClick={doPark} disabled={busy} />
          <CompanionBtn testid="companion-transfer" icon={ArrowLeftRight} label="Transfer" onClick={() => setShowXfer(true)} disabled={busy} />
          <CompanionBtn testid="companion-hangup" icon={PhoneOff} label="End" onClick={doHangup} disabled={busy} danger />
        </div>
      )}

      {onDesk && !showXfer && (
        <button data-testid="companion-pull" onClick={doPull} disabled={busy}
          className="w-full h-9 flex items-center justify-center gap-1.5 text-[12px] font-bold border-t border-white/10 hover:bg-white/5 disabled:opacity-50">
          <Smartphone className="w-3.5 h-3.5" /> Pull call to this device
        </button>
      )}
    </div>
  );
}

function CompanionBtn({ icon: Icon, label, onClick, disabled, danger, testid }) {
  return (
    <button
      data-testid={testid} onClick={onClick} disabled={disabled}
      className="flex flex-col items-center gap-1 py-2 rounded-xl transition-colors disabled:opacity-40"
      style={{ background: danger ? "rgba(239,68,68,0.9)" : "rgba(255,255,255,0.08)" }}
    >
      <Icon className="w-4 h-4" />
      <span className="text-[10px] font-semibold">{label}</span>
    </button>
  );
}
